import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { ModeSelectionScreen } from './ModeSelectionScreen';
import { QuickAssignment } from './QuickAssignment';
import { StepByStepAssignment } from './StepByStepAssignment';
import { MatrixAssignment } from './MatrixAssignment';
import { ReviewAndConfirm } from './ReviewAndConfirm';
import { AssignmentWizardProps, DomainAssignment, AssignmentResult } from './types';

const useStyles = makeStyles({
  surface: {
    maxWidth: '900px',
    minWidth: '600px',
    maxHeight: '90vh',
    ...shorthands.padding('0'),
  },
  body: {
    ...shorthands.padding('0'),
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('16px', '24px'),
    ...shorthands.borderBottom('1px', 'solid', '#e0e0e0'),
    flexShrink: 0,
  },
  closeButton: {
    cursor: 'pointer',
    ':hover': {
      opacity: 0.7,
    },
  },
  content: {
    ...shorthands.padding('24px'),
    overflowY: 'auto',
    maxHeight: 'calc(90vh - 80px)',
  },
});

type WizardScreen = 
  | 'mode-selection'
  | 'quick'
  | 'step-by-step'
  | 'matrix'
  | 'review';

export const DomainPolicyAssignmentWizard: React.FC<AssignmentWizardProps> = ({
  approvedDomains,
  isOpen,
  onClose,
  onComplete,
}) => {
  const styles = useStyles();
  const [currentScreen, setCurrentScreen] = useState<WizardScreen>('mode-selection');
  const [assignments, setAssignments] = useState<DomainAssignment[]>([]);
  const [previousScreen, setPreviousScreen] = useState<WizardScreen>('mode-selection');

  // Reset wizard state whenever it opens
  React.useEffect(() => {
    if (isOpen) {
      console.log('🔧 Wizard opened - resetting state');
      setCurrentScreen('mode-selection');
      setAssignments([]);
      setPreviousScreen('mode-selection');
    }
  }, [isOpen]);

  // Debug logging
  React.useEffect(() => {
    console.log('🔧 DomainPolicyAssignmentWizard props changed:');
    console.log('  - isOpen:', isOpen);
    console.log('  - approvedDomains:', approvedDomains);
    console.log('  - domains count:', approvedDomains?.length || 0);
  }, [isOpen, approvedDomains]);

  const handleQuickAssign = (policyIds: string[]) => {
    // Create assignments for all domains with the same policies
    const quickAssignments: DomainAssignment[] = approvedDomains.map(domain => ({
      domainId: domain.id,
      domainName: domain.domain,
      policyIds: policyIds,
      status: 'pending',
    }));
    
    setAssignments(quickAssignments);
    setCurrentScreen('review');
  };

  const handleModeSelect = (mode: 'quick' | 'step-by-step' | 'matrix') => {
    setCurrentScreen(mode);
  };

  const handleAssignmentsComplete = (completedAssignments: DomainAssignment[]) => {
    setAssignments(completedAssignments);
    setPreviousScreen(currentScreen); // Track where we came from
    setCurrentScreen('review');
  };

  const handleReviewComplete = (results: AssignmentResult[]) => {
    onComplete(assignments);
    onClose();
  };

  const handleBackFromReview = () => {
    // Go back to whichever screen we came from before the review
    if (previousScreen === 'quick' || previousScreen === 'step-by-step' || previousScreen === 'matrix') {
      setCurrentScreen(previousScreen);
    } else {
      setCurrentScreen('mode-selection');
    }
  };

  const handleClose = () => {
    // Reset state
    setCurrentScreen('mode-selection');
    setAssignments([]);
    setPreviousScreen('mode-selection');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && handleClose()} data-testid="domain-policy-assignment-dialog-root">
      <DialogSurface className={styles.surface} data-testid="domain-policy-assignment-dialog-surface">
        <DialogBody className={styles.body} data-testid="domain-policy-assignment-dialog-body">
          <div className={styles.header} data-testid="domain-policy-assignment-header">
            <DialogTitle data-testid="domain-policy-assignment-title">Domain Policy Assignment</DialogTitle>
            <Dismiss24Regular
              className={styles.closeButton}
              onClick={handleClose}
              data-testid="domain-policy-assignment-close-icon"
            />
          </div>
          
          <DialogContent className={styles.content} data-testid="domain-policy-assignment-content">
            {currentScreen === 'mode-selection' && (
              <ModeSelectionScreen
                domains={approvedDomains}
                onModeSelect={handleModeSelect}
                onSkip={handleClose}
              />
            )}

            {currentScreen === 'quick' && (
              <QuickAssignment
                domains={approvedDomains}
                onBack={() => setCurrentScreen('mode-selection')}
                onComplete={handleAssignmentsComplete}
              />
            )}

            {currentScreen === 'step-by-step' && (
              <StepByStepAssignment
                domains={approvedDomains}
                onBack={() => setCurrentScreen('mode-selection')}
                onComplete={handleAssignmentsComplete}
              />
            )}

            {currentScreen === 'matrix' && (
              <MatrixAssignment
                domains={approvedDomains}
                onBack={() => setCurrentScreen('mode-selection')}
                onComplete={handleAssignmentsComplete}
              />
            )}

            {currentScreen === 'review' && (
              <ReviewAndConfirm
                assignments={assignments}
                domains={approvedDomains}
                onBack={handleBackFromReview}
                onComplete={handleReviewComplete}
              />
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

// Export all components for potential individual use
export * from './types';
export { DomainCard } from './DomainCard';
export { PolicyMultiSelect } from './PolicyMultiSelect';
export { ModeSelectionScreen } from './ModeSelectionScreen';
export { QuickAssignment } from './QuickAssignment';
export { StepByStepAssignment } from './StepByStepAssignment';
export { MatrixAssignment } from './MatrixAssignment';
export { ReviewAndConfirm } from './ReviewAndConfirm';
